function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import dc from 'dc';
import * as d3 from 'd3';
import { BaseMixin } from '../Mixins/BaseMixin';
import { TileMixin } from '../Mixins/TileMixin';
import { AVITableTile } from '../../Layout/AVITableTile/AVITableTile';
import { withAVIDashboardContext } from '../../Context/AVIDashboard/AVIDashboard';
import './DataTable.scss';

var DataTable = /*#__PURE__*/function (_Component) {
  _inheritsLoose(DataTable, _Component);

  function DataTable(props) {
    var _this;

    _this = _Component.call(this, props) || this;

    _defineProperty(_assertThisInitialized(_this), "currentOffset", 0);

    _defineProperty(_assertThisInitialized(_this), "totalRecords", 0);

    _defineProperty(_assertThisInitialized(_this), "pageSize", 50);

    _defineProperty(_assertThisInitialized(_this), "chart", null);

    _defineProperty(_assertThisInitialized(_this), "tableData", []);

    _defineProperty(_assertThisInitialized(_this), "sortByFunc", null);

    _defineProperty(_assertThisInitialized(_this), "sortByOrder", d3.ascending);

    _this.chartRef = React.createRef();
    _this.infoRef = React.createRef(); // Bind scope of functions

    _this.updateTableInfo = _this.updateTableInfo.bind(_assertThisInitialized(_this));
    _this.previous = _this.previous.bind(_assertThisInitialized(_this));
    _this.next = _this.next.bind(_assertThisInitialized(_this));
    _this.update = _this.update.bind(_assertThisInitialized(_this));
    _this.getTableData = _this.getTableData.bind(_assertThisInitialized(_this));
    _this.getChart = _this.getChart.bind(_assertThisInitialized(_this));
    _this.alterSort = _this.alterSort.bind(_assertThisInitialized(_this));
    _this.setSortOrder = _this.setSortOrder.bind(_assertThisInitialized(_this));
    _this.doSort = _this.doSort.bind(_assertThisInitialized(_this));
    _this.onCrossfilterChange = _this.onCrossfilterChange.bind(_assertThisInitialized(_this));
    _this.state = {
      sortCol: null,
      sortOrder: 'asc'
    };
    return _this;
  }

  var _proto = DataTable.prototype;

  _proto.componentDidUpdate = function componentDidUpdate(prevProps, prevState) {
    if (prevProps.sortBy !== this.props.sortBy && this.state.sortCol !== this.props.sortBy) {
      this.setState({
        sortCol: this.props.sortBy
      });
    }

    if (prevProps.sortOrder !== this.props.sortOrder && this.state.sortOrder !== this.props.sortOrder) {
      this.setState({
        sortOrder: this.props.sortOrder
      });
    }

    if (prevState.sortCol !== this.state.sortCol || prevState.sortOrder !== this.state.sortOrder) {
      this.doSort();
    }

    this.updateTableInfo();
  };

  _proto.componentDidMount = function componentDidMount() {
    var _this$props = this.props,
        size = _this$props.size,
        sortBy = _this$props.sortBy,
        showSections = _this$props.showSections,
        columns = _this$props.columns,
        order = _this$props.order,
        section = _this$props.section,
        sortByHeaders = _this$props.sortByHeaders;
    this.chart = dc.dataTable(this.chartRef.current); // Override column functions

    this.chart._doColumnHeaderFormat = function (d) {
      if (d.title !== undefined) {
        return '<span title="' + d.title + '">' + d.label + '</span>';
      } else {
        return d.label;
      }
    };

    BaseMixin(this.chart, this.props, true);
    TileMixin(this.chart, this.props);
    this.chart.size(size).sortBy(sortBy).showSections(showSections).beginSlice(0).endSlice(50).columns(columns).order(order);

    if (section) {
      this.chart.section(section);
    }

    this.chart.render();
    this.chart.on('renderlet', this.updateTableInfo);
    this.chart.redraw();

    if (sortByHeaders) {
      this._onHeaderClick = this.onHeaderClick.bind(this);
      d3.select(this.chartRef.current).selectAll('th').on('click', this._onHeaderClick);
    }

    this.props.crossfilter.onChange(this.onCrossfilterChange);

    if (this.props.register) {
      this.props.register(this.chart, this.props.name, false);
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.props.unregister) {
      this.props.unregister(this.chart, this.props.name);
    }
  };

  _proto.onCrossfilterChange = function onCrossfilterChange(eventName) {
    if (eventName === 'dataAdded') {
      this.chart.render();
    }
  };

  _proto.onHeaderClick = function onHeaderClick(row, i) {
    this.alterSort(i);
  };

  _proto.updateTableInfo = function updateTableInfo() {
    var translations = this.props.translations;

    if (typeof this.chart.dimension().top === 'function') {
      this.tableData = this.chart.dimension().top(Infinity);
    } else {
      this.tableData = this.chart.dimension().all();
    }

    this.totalRecords = this.tableData.length;
    this.numPages = Math.ceil(this.totalRecords / this.pageSize);

    if (this.currentOffset > this.totalRecords) {
      this.currentOffset = 0;
      this.update();
      this.chart.redraw();
      return;
    }

    var startSlice = this.currentOffset + 1;
    var endSlice;

    if (this.currentOffset + this.pageSize < this.totalRecords) {
      endSlice = this.currentOffset + this.pageSize;
    } else {
      endSlice = this.totalRecords;
    }

    var dataTableInfo = translations.from + " " + startSlice + " " + translations.to + " " + endSlice + " " + translations.of + " " + this.totalRecords;

    if (this.infoRef.current !== null) {
      this.infoRef.current.innerHTML = dataTableInfo;
    }

    this.headerSort();
  };

  _proto.update = function update() {
    this.chart.beginSlice(this.currentOffset);

    if (this.currentOffset + this.pageSize > this.totalRecords) {
      this.chart.endSlice(this.totalRecords);
    } else {
      this.chart.endSlice(this.currentOffset + this.pageSize);
    }
  };

  _proto.getTableData = function getTableData() {
    var columns = this.props.columns; // Sanitize table data

    if (this.tableData.length > 0) {
      this.tableData = this.tableData.map(function (row) {
        var ro = {};
        columns.forEach(function (c) {
          ro[c.label] = c.exportFormat && c.exportFormat(row) || c.format(row);
        });
        return ro;
      });
    }

    return this.tableData;
  };

  _proto.next = function next() {
    if (this.currentOffset + this.pageSize < this.totalRecords) {
      this.currentOffset += this.pageSize;
      this.update();
      this.chart.redraw();
    }
  };

  _proto.previous = function previous() {
    if (this.currentOffset >= this.pageSize) {
      this.currentOffset -= this.pageSize;
      this.update();
      this.chart.redraw();
    } else {
      this.currentOffset = 0;
    }
  };

  _proto.getChart = function getChart() {
    return this.chart;
  };

  _proto.alterSort = function alterSort(eventOrIndex) {
    var colIdx = typeof eventOrIndex === 'number' ? eventOrIndex : eventOrIndex.target.value;

    if (this.state.sortCol === colIdx) {
      this.setState(function (prevState) {
        return {
          sortOrder: prevState.sortOrder === 'asc' ? 'desc' : 'asc'
        };
      });
    } else {
      this.setState({
        sortCol: colIdx,
        sortOrder: 'asc'
      });
    }
  };

  _proto.doSort = function doSort() {
    var _this2 = this;

    if (!(typeof this.state.sortCol === 'number')) {
      return;
    }

    var currentColumn = this.props.columns[this.state.sortCol];
    var columns = this.props.columns;

    this.sortByFunc = function (d) {
      if (currentColumn.sortField) {
        return typeof currentColumn.sortField === 'function' ? currentColumn.sortField(d) : d[currentColumn.sortField];
      }

      return +columns[_this2.state.sortCol].format(d);
    };

    if (typeof this.sortByFunc === 'function') {
      this.chart.order(this.state.sortOrder === 'desc' ? d3.descending : d3.ascending).sortBy(this.sortByFunc).redraw();
    }

    this.headerSort();
  };

  _proto.headerSort = function headerSort() {
    var _this3 = this;

    d3.select(this.chartRef.current).selectAll('th').attr('class', function (d, i) {
      return i === _this3.state.sortCol ? "sorting-by order-" + _this3.state.sortOrder : '';
    });
  };

  _proto.setSortOrder = function setSortOrder(eventOrName) {
    var order = typeof eventOrName === 'string' ? eventOrName : eventOrName.target.value;
    this.setState({
      sortOrder: order
    });
  };

  _proto.render = function render() {
    var _this$props2 = this.props,
        columns = _this$props2.columns,
        showPagingControls = _this$props2.showPagingControls,
        showSortControls = _this$props2.showSortControls,
        translations = _this$props2.translations;
    var opts = [];
    columns.forEach(function (col, colIdx) {
      opts.push( /*#__PURE__*/React.createElement("option", {
        key: 'col-' + colIdx,
        value: colIdx
      }, col.label));
    });
    var opts2 = [];
    opts2.push( /*#__PURE__*/React.createElement("option", {
      key: 'order-asc',
      value: 'asc'
    }, translations.asc));
    opts2.push( /*#__PURE__*/React.createElement("option", {
      key: 'order-desc',
      value: 'desc'
    }, translations.desc));
    return /*#__PURE__*/React.createElement(AVITableTile, {
      title: this.props.chartTitle,
      getTableData: this.getTableData,
      getChart: this.getChart,
      table: this.chartRef,
      showTools: this.props.showTools,
      toolsComponent: this.props.toolsComponent
    }, showPagingControls && /*#__PURE__*/React.createElement("div", {
      className: "avi-data-table-nav-container"
    }, /*#__PURE__*/React.createElement("p", {
      className: "avi-data-table-nav-info",
      ref: this.infoRef
    }), /*#__PURE__*/React.createElement("button", {
      className: "avi-data-table-nav-button no-print",
      onClick: this.previous
    }, translations.prevPage), /*#__PURE__*/React.createElement("button", {
      className: "avi-data-table-nav-button no-print",
      onClick: this.next
    }, translations.nextPage)), showSortControls && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("label", null, " ", translations.sortBy), /*#__PURE__*/React.createElement("select", {
      onChange: this.alterSort
    }, opts), /*#__PURE__*/React.createElement("select", {
      onChange: this.setSortOrder
    }, opts2)), /*#__PURE__*/React.createElement("div", {
      className: "avi-data-table--wrapper"
    }, /*#__PURE__*/React.createElement("table", {
      className: "avi-data-table",
      ref: this.chartRef
    })));
  };

  return DataTable;
}(Component);

_defineProperty(DataTable, "defaultProps", {
  // group: d => d.key, // default group is messing with sorting
  order: d3.ascending,
  sortBy: null,
  sortOrder: null,
  size: 250,
  showSections: false,
  showSortControls: false,
  sortByHeaders: true,
  translations: {
    from: 'Viser',
    to: 'til',
    of: 'av',
    sortBy: 'Sorter etter: ',
    nextPage: 'Neste side',
    prevPage: 'Forrige side ',
    asc: 'Stigende',
    desc: 'Synkende'
  },
  showTools: false,
  showPagingControls: true
});

DataTable.propTypes = process.env.NODE_ENV !== "production" ? {
  section: PropTypes.func,
  columns: PropTypes.array,
  order: PropTypes.func,
  sortBy: PropTypes.any,
  sortOrder: PropTypes.string,
  size: PropTypes.number,
  showSections: PropTypes.bool,
  chartTitle: PropTypes.string,
  showPagingControls: PropTypes.bool,
  showSortControls: PropTypes.bool,
  sortByHeaders: PropTypes.bool,
  translations: PropTypes.object,

  /** If true, tools to reset or download chart as SVG or PNG is shown. */
  showTools: PropTypes.bool,

  /**
   * If set, this component is rendered instead of standard tools.
   *
   * Callbacks for performing the default actions are provided as props.
   *
   * @param onDownloadXLS Download table as XLS
   */
  toolsComponent: PropTypes.any
} : {};
export default withAVIDashboardContext(DataTable);